import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { areTimeSlotOverlapping } from '../datetime/generate-start-times-for-multiple-overlapping-timeslots.js';
import { TimeSlot } from '../datetime/time-slot.js';
import { Institution } from '../entities/institution.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Product } from '../entities/products.js';
import {
  BookingResult,
  RegistrationEinzelberatungData,
  RegistrationProductData,
  RegistrationWorkshopData,
} from '../entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Role } from '../enums/roles.js';
import { InvalidInstitutionIDError } from '../errors/InvalidInstitutionIDError.js';
import { RegistrationAuthorizationNeededError } from '../errors/RegistrationAuthorizationNeededError.js';
import { RegistrationInvalidError } from '../errors/RegistrationInvalidError.js';
import { RegistrationLdapNeededError } from '../errors/RegistrationLdapNeededError.js';
import { RegistrationMaintenanceError } from '../errors/RegistrationMaintenanceError.js';
import { RegistrationSuspendedError } from '../errors/RegistrationSuspendedError.js';
import { TimeslotsAreoverlappingError } from '../errors/TimeslotsAreoverlappingError.js';
import { nachrueckPossible } from '../logic/participation.js';
import { notifyBestaetigungWorkshopRegistrierung } from '../notification/bestaetigung-workshop-registrierung.js';
import { createPayment } from '../payment.js';
import { getPluginForScope } from '../plugins/plugin.js';

async function getAndAssertInstitution(institutionID: string) {
  if (!institutionID) throw new RegistrationInvalidError();

  const institution = await getByID(
    Institution,
    institutionID,
  )({
    _id: 1,
    isLDAPOnly: 1,
    maintenanceMode: 1,
  });
  if (!institution) throw new InvalidInstitutionIDError(institutionID);
  if (institution.maintenanceMode) throw new RegistrationMaintenanceError();

  return institution;
}

export async function _book(
  user: NarrowedEntity<
    User,
    {
      _id: 1;
      email: 1;
      title: 1;
      username: 1;
      firstName: 1;
      lastName: 1;
      isLdapAccount: 1;
      whatsappNotification: 1;
      suspentions: { _id: 1; institutionID: 1 };
      whatsappPhone: 1;
    }
  >,
  productData: Maybe<RegistrationProductData[]>,
  paymentMethod: Maybe<string>,
) {
  // check nevertheless because public API
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!productData) throw new RegistrationInvalidError();

  const productIDs = productData.map(d => d.productID as string);
  const products = await getByID(
    Product,
    productIDs,
  )({ _id: 1, isLDAPOnly: 1, leftCapacity: 1, hasPrice: 1, prices: { unitAmount: 1 }, institutionID: 1 });
  const productsToBuy = products.filter(product => {
    if (product instanceof Workshop) {
      if (product.leftCapacity === 0) return false;
    }
    return true;
  });

  const institutionID = products[0]?.institutionID;
  if (!institutionID) throw new Error('id should be defined');

  if (products.some(product => product.institutionID !== institutionID))
    throw new Error('ordering products from more than one institution at the same time is not supported');

  const institution = await getAndAssertInstitution(institutionID);
  const userID = user._id;

  const paymentPlugin = getPluginForScope('payment', institutionID);

  // ? Do we even need to fetch the stammdaten here?
  const userStammdaten = await UserStammdaten.getByUserAndInstitution(user._id, institutionID)({ _id: 1 });
  if (!userStammdaten) throw new Error('stammdaten not found');

  // handle suspensions for maybe already existing users
  if (user.suspentions.map(suspension => suspension.institutionID).includes(institutionID))
    throw new RegistrationSuspendedError();

  // check for ldap account
  const hasLdapOnlyWorkshop = products.some(workshop => workshop.isLDAPOnly);
  if ((institution.isLDAPOnly || hasLdapOnlyWorkshop) && user.isLdapAccount) throw new RegistrationLdapNeededError();

  // check for product-entry requirements
  // TODO: find out why this causes a cicular dependency
  const m = await import('../api/product-entry-requirement.js');
  await Promise.all(
    productData.map(async productDataItem =>
      m.checkWeatherUserMeetsEntryRequirementsQuery(user._id, productDataItem.productID as string)(),
    ),
  );

  // check for payment method
  if (productsToBuy.some(product => product.hasPrice) && !paymentMethod)
    throw new Error('payment required but paymentmethod is undefined');

  // Create participations

  const participationSelectionSet = {
    _id: 1,
    state: 1,
    userID: 1,
    productID: 1,
    user: { suspentions: { institutionID: 1 } },
    product: { institutionID: 1 },
  } as const;
  const participations: NarrowedEntity<Participation, typeof participationSelectionSet>[] = [];

  const allUserParticipationsData = await loadMongoData(Participation, { userID });
  const allUserParticipations = await getByID(
    Participation,
    allUserParticipationsData.map(pd => pd._id),
  )({
    _id: 1,
    _class: 1,
    productID: 1,
    timeslotID: 1,
    state: 1,
    product: {
      _id: 1,
      leftCapacity: 1,
      appointments: {
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
        meetinLink: { link: 1 },
      },
      slots: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
    },
  });

  const checkForOverlappingTimeSlots = async (testSlots: TimeSlot[]) => {
    testSlots = testSlots.filter(slot => slot.start && slot.end); // filter out 'tbd' slots
    if (testSlots.length === 0) return;

    // check testslots against each other
    if (areTimeSlotOverlapping(testSlots)) throw new TimeslotsAreoverlappingError();

    const ss1 = await Promise.all(participations.map(p => p.timespan())).then(sls => sls.flat());
    const ss2 = await Promise.all(
      allUserParticipations
        .filter(p => p.state === ParticipationState.PARTICIPATED || p.state === ParticipationState.IN)
        .map(p => p.timespan()),
    ).then(sls => sls.flat());
    const timeslots = [...ss1, ...ss2].reduce((acc, timeslot) => {
      // filter out duplicates
      if (!!acc.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
      if (!!testSlots.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
      return [...acc, timeslot];
    }, [] as TimeSlot[]);

    // test against all timeslots
    // NOTE: test individually so we do not report overlappings between two already booked appointments
    for (const testSlot of testSlots) {
      for (const timeslot of timeslots) {
        if (areTimeSlotOverlapping([testSlot, timeslot])) throw new TimeslotsAreoverlappingError();
      }
    }
  };

  await Promise.all(
    productData.map(async productDataItem => {
      // participation may already exists because user could have canceled a previous registration
      const getOrCreateParticipation = async () => {
        const productID = productDataItem.productID;

        const filter =
          productDataItem instanceof RegistrationEinzelberatungData
            ? { userID, productID, timeslotID: productDataItem.timeslotID }
            : { userID, productID };

        const existingParticipation = (await loadMongoData(Participation, filter))[0];
        if (existingParticipation)
          switch (existingParticipation.state) {
            case ParticipationState.IN:
              logger.error({ existingParticipation }, 'can not order again');
              throw new Error("participation is already set to 'in' cant order again");
            case ParticipationState.WAITING:
              logger.error({ existingParticipation }, 'can not order again');
              throw new Error("participation state is 'waiting' cant order again");
            case ParticipationState.SUSPENDED:
              logger.error({ existingParticipation }, 'can not order again');
              throw new Error("participation state is 'suspended' cant order again");
            case ParticipationState.LEFT:
              // this is fine
              break;
            default:
              throw new Error('unreachable');
          }

        if (productDataItem instanceof RegistrationWorkshopData) {
          const participation = existingParticipation
            ? new ParticipationWorkshop(existingParticipation)
            : new ParticipationWorkshop({ _id: A.getGloballyUniqueID(), ...filter });

          // ! this is not proper plugin usage
          if (paymentPlugin.id === 'payment::sd') {
            // internal studium-digitale should not get invoices
            if (user?.email.endsWith('@studiumdigitale.uni-frankfurt.de')) participation.ikh_doNotSendInvoice = true;
          }

          // set state
          const workshop = await getByID(
            Workshop,
            productID as string,
          )({
            _id: 1,
            institutionID: 1,
            isCanceled: 1,
            leftCapacity: 1,
            nachrueckBlockTimeForLowPriorityInDays: 1,
            appointments: { start: 1 },
            settings: {
              blockAutomaticMoveUpDaysInAdvance: 1,
              inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
            },
          });
          if (!workshop) throw new Error('something went wrong');

          // TODO: find out why this causes a cicular dependency
          const m = await import('../api/product-waitinglist-priority-rule.js');
          participation.nachrueckPriority = await m.getWaitinglistPriorityForUserForProduct(userID, workshop._id);
          participation.state = ParticipationState.WAITING;
          participation.product = workshop;
          if (nachrueckPossible(participation)) participation.state = ParticipationState.IN;

          // check for overlaping appointments
          const testSlots = await participation.timespan();
          await checkForOverlappingTimeSlots(testSlots);

          if (existingParticipation) await participation.save();
          else await participation.create();

          return participation;
        }

        if (productDataItem instanceof RegistrationEinzelberatungData) {
          const participation = existingParticipation
            ? new ParticipationEinzelberatung(existingParticipation)
            : new ParticipationEinzelberatung({ _id: A.getGloballyUniqueID(), ...filter });

          participation.state = ParticipationState.IN;

          // check if slot is still free
          const pCol = await getRawCollection(ParticipationEinzelberatung._collectionName);
          const otherExistingParticipations = await pCol
            .find<ParticipationEinzelberatung>({
              timeslotID: participation.timeslotID,
            })
            .toArray()
            .then(ps => ps.filter(p => p._id !== participation._id));

          const slotIsAlreadyFilled = otherExistingParticipations.some(p => p.state === ParticipationState.IN);
          if (slotIsAlreadyFilled) throw new Error('cant enroll; slot is already filled');

          // delete other participations (so users cant try to reregister again)
          await pCol.deleteMany({ _id: { $in: otherExistingParticipations.map(p => p._id) } });

          // check for overlaping appointments
          const testSlots = await participation.timespan();
          await checkForOverlappingTimeSlots(testSlots);

          if (existingParticipation) await participation.save();
          else await participation.create();

          return participation;
        }

        throw new Error('unreachable; unknown productDataItem: ' + productDataItem?._class);
      };

      const p = await getOrCreateParticipation();
      participations.push(p!);
    }),
  );
  // check for suspensions
  participations.forEach(p => p.failWhenSuspended());

  // add participant role
  await runInContext(serverContext.sessionID, () =>
    assignRoleToUser(user._id, Role.PARTICIPANT, [institutionID], User),
  );

  const prices = await Promise.all(productsToBuy.map(p => paymentPlugin.getProductPrice(p._id, user._id)));
  const totalPrice = prices.map(p => p?.unitAmount ?? 0).reduce((sum, amount) => sum + amount, 0);
  const paymentRequired = totalPrice > 0;

  if (paymentRequired) void 0; // in case payment is required => send Mails after payment
  else await notifyBestaetigungWorkshopRegistrierung(institution._id, user, participations);

  // prepare result
  const participationIDs = participations.map(participation => participation._id);
  const loginToken = await generateLoginToken({ userID: user._id, userClass: User._class, ttu: 1 });
  const result = new BookingResult({ loginToken, paymentRequired, participationIDs });

  // maybe create payment
  if (paymentRequired) {
    if (!paymentMethod) throw new Error('payment required but paymentmethod is undefined');
    const paymentIDs = await createPayment(institutionID, participationIDs, paymentMethod);
    result.paymentIDs = paymentIDs;
  }

  // set waitin=List Place (this may reorderder the waitinglist because of priority)
  // TODO: move booking into buchungsheld entrypoint to avoid circular dependency
  const m2 = await import('../../../buchungsheld/src/server/waitinglist-monitoring.js');
  for (const productID of productIDs) await m2.nachrueckLogic(productID);

  // notify plugin
  await Promise.all(
    participations.map(async participation => {
      if (participation.state === ParticipationState.IN) await paymentPlugin.afterEnrollment(participation._id);
    }),
  );

  return result;
}

/**
 * Can be called multiple times for one booking session (i.e. if user requires authentication)
 * @returns loginToken to allow client to login user to show booking results in profile
 */
export const book = registerMutation({
  operationName: 'book',
  params: [
    { name: 'productData', type: () => [RegistrationProductData] },
    { name: 'paymentMethod', type: () => String },
  ],
  type: () => BookingResult,
  resolve: (productData, paymentMethod) => async (gqlFields: BaseQLSelectionSet<BookingResult>) => {
    const user = await CurrentUserInfo.getMyself<User>()({
      _id: 1,
      email: 1,
      title: 1,
      username: 1,
      firstName: 1,
      lastName: 1,
      isLdapAccount: 1,
      whatsappNotification: 1,
      suspentions: { _id: 1, institutionID: 1 },
      whatsappPhone: 1,
    });
    if (!user) throw new Error('user has to be logged in to book');

    // ensure authenticated session
    // allow server context to book for any user
    if (!context.serverContext && user._id !== context.userID) throw new RegistrationAuthorizationNeededError(user._id);

    return _book(user, productData, paymentMethod);
  },
});
